import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LifeBuoy, Send, Lightbulb } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/download";

const area = "w-full border border-slate-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-gold";
const STATUS = { open: "In attesa di risposta", answered: "Risposta ricevuta", closed: "Chiusa" };
const when = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });

const HelpHistory = ({ items }) => (items.length ? (
  <div className="mt-6 space-y-3 max-h-64 overflow-y-auto pr-1" data-testid="help-history">
    <div className="overline text-slate-400">Le tue richieste</div>
    {items.map((r) => (
      <div key={r.id} className="border border-slate-200 rounded-xl p-3 text-sm" data-testid={`help-item-${r.id}`}>
        <div className="flex justify-between gap-2 text-xs text-slate-500"><span>{when(r.createdAt)}</span><span className="font-semibold">{STATUS[r.status]}</span></div>
        <p className="mt-1 whitespace-pre-line">{r.message}</p>
        {r.replies.map((x, i) => <div key={i} className="mt-2 pl-3 border-l-2 border-gold bg-[#FBF6EE] rounded-r-lg py-2 pr-2" data-testid={`help-reply-${r.id}-${i}`}><div className="text-xs font-semibold text-aic-navy">Supporto · {when(x.at)}</div><p className="whitespace-pre-line">{x.text}</p></div>)}
      </div>
    ))}
  </div>
) : null);

export const HelpButton = () => {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("aiuto") === "1");
  const [text, setText] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/support/help/mine").then((r) => setItems(r.data));
  useEffect(() => { if (params.get("aiuto") === "1") { setOpen(true); } }, [params]);
  useEffect(() => { if (open) { load(); } }, [open]);
  const close = (o) => { setOpen(o); if (!o && params.get("aiuto")) { params.delete("aiuto"); setParams(params, { replace: true }); } };
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/support/help", { text }); setText(""); toast.success("Richiesta inviata: ti risponderemo qui e con una notifica"); load(); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (<>
    <button onClick={() => setOpen(true)} data-testid="dashboard-help-btn" className="btn-pill-outline text-sm"><LifeBuoy className="w-4 h-4"/>Aiuto</button>
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="help-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Come possiamo aiutarti?</DialogTitle>
          <DialogDescription>Descrivi liberamente il problema: la richiesta arriva all'amministrazione del portale.</DialogDescription></DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <textarea required minLength={5} maxLength={3000} rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="es. Non riesco a caricare la locandina del mio evento…" data-testid="help-text" className={area}/>
          <button disabled={busy || text.trim().length < 5} data-testid="help-submit" className="btn-pill-gold text-sm disabled:opacity-50"><Send className="w-4 h-4"/>{busy ? "Invio…" : "Invia richiesta"}</button>
        </form>
        <HelpHistory items={items}/>
      </DialogContent>
    </Dialog>
  </>);
};

export const SuggestionsCard = () => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/support/suggestions", { text }); setText(""); toast.success("Grazie! Il tuo suggerimento è arrivato all'amministrazione"); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={send} className="mt-10 rounded-2xl border border-dashed border-gold/60 bg-[#FBF6EE] p-5 flex flex-col md:flex-row gap-3 md:items-end" data-testid="suggestions-card">
      <label className="flex-1 text-sm font-semibold text-aic-navy"><span className="flex items-center gap-2 mb-2"><Lightbulb className="w-4 h-4 text-gold"/>Hai suggerimenti per migliorare il portale?</span>
        <textarea required minLength={5} maxLength={3000} rows={2} value={text} onChange={(e) => setText(e.target.value)} data-testid="suggestion-text" className={`${area} font-normal bg-white`}/></label>
      <button disabled={busy || text.trim().length < 5} data-testid="suggestion-submit" className="btn-pill-gold text-sm disabled:opacity-50 shrink-0">{busy ? "Invio…" : "Invia suggerimento"}</button>
    </form>
  );
};
